/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Focused Practice Results Page - Display results with question review (UC-115)
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import {
  Target,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ListOrdered,
  ArrowUpRight,
  AlertTriangle
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import { renderContent } from '../utils/contentRenderer'

const FocusedPracticeResults = () => {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { results, loading, error, getResults, resetSession } = useFocusedPracticeStore()

  const [isResetting, setIsResetting] = useState(false)

  useEffect(() => {
    loadResults()
  }, [sessionId])

  const loadResults = async () => {
    const result = await getResults(sessionId)
    if (!result.success) {
      toast.error(getErrorMessage(result.error))
    }
  }

  const handleRedo = async () => {
    setIsResetting(true)
    try {
      const result = await resetSession(sessionId)

      if (result.success) {
        toast.success('Session reset! Starting practice again...')
        navigate(`/focused-practice/${sessionId}/review`)
      } else {
        toast.error(getErrorMessage(result.error) || 'Failed to reset session')
      }
    } catch (err) {
      toast.error('Failed to reset session')
    } finally {
      setIsResetting(false)
    }
  }

  const getAnswerLabel = (answer) => {
    if (answer === null || answer === undefined) return '-'
    // Handle array answers (e.g., ["A"] from correct_answer)
    if (Array.isArray(answer)) {
      return answer.length > 0 ? getAnswerLabel(answer[0]) : '-'
    }
    // Convert to string if number or other type
    const answerStr = String(answer)
    // Handle letter answers (A, B, C, D)
    if (answerStr.length === 1 && /[A-D]/i.test(answerStr)) {
      return answerStr.toUpperCase()
    }
    return answerStr
  }

  if (loading && !results) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  if (error && !results) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600">{getErrorMessage(error)}</p>
          <button
            onClick={() => navigate('/focused-practice')}
            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            Back to List
          </button>
        </div>
      </div>
    )
  }

  const session = results?.session
  const comparison = results?.comparison || {}
  const questionDetails = results?.questionDetails || []
  const stillStruggling = results?.stillStruggling || []
  const recommendations = results?.recommendations || []

  // Tutor-assigned homework has no baseline ("before") accuracy, so the before/after comparison is
  // meaningless — show a single accuracy figure instead.
  const hasComparison = comparison.before != null
  const improvement = comparison.improvement || 0
  const isImproved = improvement > 0
  const beforeAccuracy = comparison.before || 0
  const afterAccuracy = comparison.after || 0

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/focused-practice')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Target className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {session?.name || 'Practice Results'}
              </h1>
              <p className="text-sm text-gray-600">{session?.targetDomain}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Accuracy Comparison Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {hasComparison ? 'Your Progress' : 'Your Accuracy'}
        </h2>

        {hasComparison ? (
          <div className="grid grid-cols-3 gap-4">
            {/* Before */}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Before</p>
              <p className="text-3xl font-bold text-gray-900">{beforeAccuracy}%</p>
              <p className="text-xs text-gray-500">Source accuracy</p>
            </div>

            {/* Improvement Arrow */}
            <div className="flex flex-col items-center justify-center">
              <div
                className={`p-3 rounded-full ${isImproved ? 'bg-green-100' : improvement < 0 ? 'bg-red-100' : 'bg-gray-100'}`}
              >
                {isImproved ? (
                  <TrendingUp className="h-8 w-8 text-green-600" />
                ) : improvement < 0 ? (
                  <TrendingDown className="h-8 w-8 text-red-600" />
                ) : (
                  <ArrowUpRight className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <p
                className={`mt-2 text-lg font-bold ${isImproved ? 'text-green-600' : improvement < 0 ? 'text-red-600' : 'text-gray-500'}`}
              >
                {improvement > 0 ? '+' : ''}
                {improvement}%
              </p>
            </div>

            {/* After */}
            <div className="text-center p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-600 mb-1">After</p>
              <p className="text-3xl font-bold text-primary-700">{afterAccuracy}%</p>
              <p className="text-xs text-primary-500">Practice accuracy</p>
            </div>
          </div>
        ) : (
          <div className="text-center p-4 bg-primary-50 rounded-lg">
            <p className="text-3xl font-bold text-primary-700">{afterAccuracy}%</p>
            <p className="text-xs text-primary-500">Practice accuracy</p>
          </div>
        )}
      </div>

      {/* Still Struggling Skills */}
      {stillStruggling.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-900">Skills to Focus On</h2>
          </div>
          <ul className="space-y-2">
            {stillStruggling.map((skill) => (
              <li key={skill.skillId} className="flex items-center justify-between">
                <span className="text-amber-800">{skill.name}</span>
                <span className="text-sm text-amber-600">{skill.accuracy}% accuracy</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-semibold text-primary-900 mb-3">Recommendations</h2>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-primary-800">
                <CheckCircle2 className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Question Review List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <ListOrdered className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Question Review</h2>
        </div>

        {questionDetails.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Question details are being loaded...</p>
        ) : (
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
            {questionDetails.map((q, idx) => (
              <div
                key={q.questionId}
                className={`p-4 rounded-lg border ${
                  q.isCorrect ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'
                }`}
              >
                {/* Question Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium ${
                        q.isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                      {q.difficulty === 'E' ? 'Easy' : q.difficulty === 'M' ? 'Medium' : 'Hard'}
                    </span>
                    {q.skillName && <span className="text-xs text-gray-500">{q.skillName}</span>}
                  </div>
                  {q.isCorrect ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                </div>

                {/* Question Text */}
                <div className="prose prose-sm max-w-none mb-4 text-gray-900">
                  {renderContent(q.questionText, q.content_type)}
                </div>

                {/* Answers */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className={`p-3 rounded-lg ${q.isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className="text-xs font-medium text-gray-500 mb-1">Your Answer</p>
                    <p
                      className={`font-semibold ${q.isCorrect ? 'text-green-700' : 'text-red-700'}`}
                    >
                      {getAnswerLabel(q.userAnswer)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">Correct Answer</p>
                    <p className="font-semibold text-green-700">
                      {getAnswerLabel(q.correctAnswer)}
                    </p>
                  </div>
                </div>

                {/* Rationale */}
                {q.rationale && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-1">Explanation</p>
                    <div className="prose prose-sm max-w-none text-gray-700">
                      {renderContent(q.rationale, q.content_type)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/focused-practice')}
          className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Back to List
        </button>

        <button
          onClick={handleRedo}
          disabled={isResetting}
          className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isResetting ? (
            <>
              <LoadingSpinner size="sm" className="text-white" />
              Resetting...
            </>
          ) : (
            <>
              <RefreshCw className="h-5 w-5" />
              Redo Practice
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default FocusedPracticeResults
